import { makeClient } from "@/contexts/apollo-context";
import type { TransactionByTransactionHashQuery } from "@/graphql/codegen/graphql";
import { TransactionByTransactionHashDocument } from "@/graphql/codegen/graphql";

type WaitForTxnPayload = {
  transactionHash: string;
  maxAttempts?: number;
};

export const waitForTxn = async (payload: WaitForTxnPayload): Promise<void> => {
  const { transactionHash, maxAttempts = 250 } = payload;

  let attempts = 0;
  const pollInterval = 300; // Milliseconds between each poll

  while (attempts < maxAttempts) {
    const res = await makeClient().query<TransactionByTransactionHashQuery>({
      query: TransactionByTransactionHashDocument,
      variables: {
        transactionHash,
      },
      fetchPolicy: "network-only",
    });

    if (res.data?.transactionByTransactionHash) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, pollInterval));

    attempts++;
  }

  return Promise.reject(
    new Error(
      `Transaction not confirmed after maximum attempts: ${transactionHash}`,
    ),
  );
};

export const isLocalBackend = () => {
  return process.env.NEXT_PUBLIC_CORE_DESO_NODE?.startsWith(
    "http://localhost:",
  );
};

type RetryOptions<T> = {
  action: () => Promise<T>;
  checkSuccess: (result: T) => boolean;
  maxAttempts?: number;
  delayMs?: number;
};

export async function retry<T>({
  action,
  checkSuccess,
  maxAttempts = 10,
  delayMs = 1000,
}: RetryOptions<T>): Promise<T> {
  let attempts = 0;

  while (attempts < maxAttempts) {
    const result = await action();

    if (checkSuccess(result)) {
      return result;
    }

    attempts++;
    if (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  throw new Error("Max attempts reached without success");
}
